import { getDateFnsLocale, getTZAbbr } from '@modernloop/shared/datetime';
import {
  add,
  addDays,
  addHours,
  addMilliseconds,
  areIntervalsOverlapping,
  differenceInCalendarDays as dateFnsDifferenceInCalendarDays,
  endOfDay as dateFnsEndOfDay,
  isSameDay as dateFnsIsSameDay,
  isWeekend as dateFnsIsWeekend,
  startOfDay as dateFnsStartOfDay,
  format,
  isAfter,
  isBefore,
  isEqual,
  isWithinInterval,
  parseISO,
  subMilliseconds,
} from 'date-fns';
import { format as formatTZ, fromZonedTime, getTimezoneOffset, toZonedTime } from 'date-fns-tz';

import { CompanyHolidaysType } from 'src/hooks/useCompanyHolidays';

type DateInput = string | number | Date;

/**
 * Check if {date} is same or before {toCompare}
 * @param date
 * @param toCompare
 */
export function sameOrBefore(date: Date, toCompare: Date): boolean {
  return isEqual(date, toCompare) || isBefore(date, toCompare);
}

/**
 * Check if {date} is same or after {toCompare}
 * @param date
 * @param toCompare
 */
export function sameOrAfter(date: Date, toCompare: Date): boolean {
  return isEqual(date, toCompare) || isAfter(date, toCompare);
}

/**
 * Check if start {date} is between {toCompareStart} and {toCompareEnd}
 * @param date
 * @param toCompareStart
 * @param toCompareEnd
 */
function isBetweenDate(date: Date, toCompareStart: Date, toCompareEnd: Date): boolean {
  const isStartDateOverlapping = isAfter(date, toCompareStart);
  const isEndDateOverlapping = isBefore(date, toCompareEnd);
  return isStartDateOverlapping && isEndDateOverlapping;
}

/**
 * Check if date blocks are overlapping on calendar
 * exclusive of start time next block.
 * @param dateStart
 * @param dateEnd
 * @param toCompareStart
 * @param toCompareEnd
 */
export function isOverlappingOnCalendar(
  dateStart: Date,
  dateEnd: Date,
  toCompareStart: Date,
  toCompareEnd: Date
): boolean {
  const adjustedEndDate = subMilliseconds(dateEnd, 1);
  return (
    isBetweenDate(dateStart, toCompareStart, toCompareEnd) ||
    isBetweenDate(adjustedEndDate, toCompareStart, toCompareEnd)
  );
}

/**
 * Utility to quickly check validity of date object.
 * @param date
 */
function isValidDate(date) {
  try {
    date.toISOString();
    return true;
  } catch {
    return false;
  }
}

/**
 * checks if start and end dates overlap company holidays
 * @param startAt, start date
 * @param endAt, end date
 * @param holidays - company holidays
 * @returns { isHoliday: boolean, holiday?: CompanyHolidaysType }
 */
export function isHoliday(
  startAt: Date,
  endAt: Date,
  holidays: CompanyHolidaysType
): { isHoliday: boolean; holidays: CompanyHolidaysType } {
  let isOff = false;
  const offs: CompanyHolidaysType = [] as CompanyHolidaysType;
  if (!holidays) {
    return { isHoliday: isOff, holidays: offs };
  }

  for (const holiday of holidays) {
    const parsedStart = parseISO(holiday.start);
    const parsedEnd = addDays(parseISO(holiday.end), 1);

    if (isOverlappingOnCalendar(startAt, endAt, parsedStart, parsedEnd)) {
      isOff = true;
      offs.push(holiday);
    }
  }
  return { isHoliday: isOff, holidays: offs };
}

/**
 * Convert  zoned LocalDate to UTC
 * @param localDate
 * @param startHour
 * @param endHour
 * @param localTimeZone
 */
export function convertLocalDateTimeToUTC(
  localDate: Date,
  startHour: string,
  endHour: string,
  localTimeZone: string
): { utcDay: string; utcStartDateTime: string; utcEndDateTime: string } | null {
  // not a valid date, nothing to do
  if (!isValidDate(localDate)) {
    return null;
  }
  // make sure date is always at the start of the day
  localDate.setHours(0, 0, 0, 0);
  // calculate startDateTime, endDateTime
  const [startHr, startMin] = startHour.split(':').map((s) => parseInt(s, 10));
  const [endHr, endMin] = endHour.split(':').map((s) => parseInt(s, 10));
  // localStartDateTime
  const localStartDateTime = new Date(localDate.getTime());
  localStartDateTime.setHours(startHr, startMin, 0, 0);
  // localEndDateTime
  const localEndDateTime = new Date(localDate.getTime());
  localEndDateTime.setHours(endHr, endMin, 0, 0);
  return {
    utcDay: fromZonedTime(localDate, localTimeZone).toISOString(),
    utcStartDateTime: fromZonedTime(localStartDateTime, localTimeZone).toISOString(),
    utcEndDateTime: fromZonedTime(localEndDateTime, localTimeZone).toISOString(),
  };
}

const now = (): Date => {
  return new Date();
};

export const isBeforeNow = (date: Date): boolean => {
  // https://date-fns.org/v2.22.1/docs/isBefore
  return isBefore(date, now());
};

export const isWithinXHoursFromNow = (date: Date, hours: number): boolean => {
  // https://date-fns.org/v2.22.1/docs/isWithinInterval
  return isWithinInterval(date, { start: now(), end: addHours(now(), hours) });
};

const isValidInterval = (interval: { start: Date; end: Date }): boolean => {
  return interval.start.getTime() <= interval.end.getTime();
};

const isIntervalOverlappingXHoursFromNow = (interval: { start: Date; end: Date }, hours: number): boolean => {
  if (!isValidInterval(interval)) {
    return false;
  }
  // https://date-fns.org/v2.22.1/docs/areIntervalsOverlapping
  return areIntervalsOverlapping(interval, { start: now(), end: addHours(now(), hours) });
};

export const isIntervalOverlappingTwelveHoursFromNow = (interval: { start: Date; end: Date }): boolean => {
  return isIntervalOverlappingXHoursFromNow(interval, 12);
};

export const formatRange = (
  start: DateInput,
  end: DateInput,
  timezone: string,
  dateFormat = 'EEEE, MMMM d, yyyy',
  timeFormat = 'h:mm aaa',
  multidayDateFormat = dateFormat,
  multidayTimeFormat = timeFormat
): string => {
  const startTime = toZonedTime(start, timezone);
  const endTime = toZonedTime(end, timezone);

  let result = '';
  if (dateFnsIsSameDay(startTime, endTime)) {
    result = `${format(startTime, `${dateFormat}${dateFormat ? ',' : ''} ${timeFormat}`)} - ${format(
      endTime,
      timeFormat
    )}`;
  } else {
    result = `${format(
      startTime,
      `${multidayDateFormat}${multidayDateFormat ? ',' : ''} ${multidayTimeFormat}`
    )} - ${format(endTime, `${multidayDateFormat}${multidayDateFormat ? ',' : ''} ${multidayTimeFormat}`)}`;
  }

  return result;
};

/**
 * Formats a date using the specified timezone
 * @param date - Date to format
 * @param formatString - https://date-fns.org/v2.24.0/docs/format
 * @param tz - the timezone
 * @deprecated
 */
export const formatToTimeZone = (date: DateInput, formatString: string, tz: string): string => {
  const zonedDate = toZonedTime(date, tz);
  return formatTZ(zonedDate, formatString, { timeZone: tz, locale: getDateFnsLocale() });
};

/**
 * Checks if same day using timezone information
 * @param date - Date to format
 * @param formatString - https://date-fns.org/v2.24.0/docs/format
 * @param tz - the timezone
 */
export const isSameDay = (dateLeft: DateInput, dateRight: DateInput, tz: string): boolean => {
  const startTime = toZonedTime(dateLeft, tz);
  const endTime = toZonedTime(dateRight, tz);
  return dateFnsIsSameDay(startTime, endTime);
};

export const isWeekend = (dateInput: DateInput, tz: string): boolean => {
  const date = toZonedTime(dateInput, tz);
  return dateFnsIsWeekend(date);
};

/** @deprecated */
export const startOfDay = (dateInput: DateInput, tz: string): Date => {
  const date = toZonedTime(dateInput, tz);
  const start = dateFnsStartOfDay(date);
  return fromZonedTime(start, tz);
};

export const endOfDay = (dateInput: DateInput, tz: string): Date => {
  const date = toZonedTime(dateInput, tz);
  const end = dateFnsEndOfDay(date);
  return fromZonedTime(end, tz);
};

export const startOfNextDay = (dateInput: DateInput, tz: string): Date => {
  const date = startOfDay(dateInput, tz);
  return add(date, { days: 1 });
};

/**
 * Returns the formatted timezone offset string
 */

export const getTimezoneOffsetStr = (timezone: string): string => {
  if (!timezone) {
    return '';
  }
  return getTZAbbr(timezone);
};

export const differenceInCalendarDays = (dateLeft: DateInput, dateRight: DateInput, tz: string): number => {
  const dateLeftTz = toZonedTime(dateLeft, tz);
  const dateRightTz = toZonedTime(dateRight, tz);

  return dateFnsDifferenceInCalendarDays(dateLeftTz, dateRightTz);
};

/**
 * If the local time object passed has 14:30 then it will return time which translates to 14:30 in the timezone.
 * E.g. time ('2021-12-12T09:00:00.000Z') represents 2:30 PM in 'Asia/Kolkata' (local time) and timezone is 'America/Los_Angeles'
 *      then it will return ('2021-12-12T22:30:00.000Z') which is 2:30 PM in 'America/Los_Angeles'
 * @param time in local timezone
 * @param timezone to which the time needs to be returned in.
 * @param hours the hour value of the time returned.
 * @param minutes the minute value of the time returned.
 * @returns Returns the time in the timezone represented by hours and minutes.
 * @deprecated
 */
export const getTimeInTimezone = (time: Date, timezone: string, hours?: number, minutes?: number): Date => {
  const result = new Date(Date.UTC(time.getFullYear(), time.getMonth(), time.getDate()));
  result.setUTCHours(hours ?? time.getHours(), minutes ?? time.getMinutes(), 0, 0);
  return addMilliseconds(result, -getTimezoneOffset(timezone, result));
};
